import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {AlertTriangle, CheckCircle2, Calendar, ArrowRight, Droplets, Bug, Utensils, Syringe} from 'lucide-react';
import BookingModal from '../components/BookingModal';

const SoutheastAsia = () => {
  const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);
  const healthRisks = [
    {
      icon: <Bug className="w-6 h-6" />,
      title: "Mosquito-Borne Diseases",
      desc: "Dengue, Japanese encephalitis, and malaria in certain rural areas."
    },
    {
      icon: <Utensils className="w-6 h-6" />,
      title: "Food & Water Safety",
      desc: "Typhoid, hepatitis A, and travellers' diarrhea from contaminated sources."
    },
    {
      icon: <Syringe className="w-6 h-6" />,
      title: "Animal Exposure Risk",
      desc: "Rabies from stray dogs, monkeys, and bats in rural and temple areas."
    }
  ];

  const vaccinesDiscuss = [
    { name: "Hepatitis A", priority: "Highly Recommended", note: "Protection against contaminated food and water" },
    { name: "Typhoid", priority: "Highly Recommended", note: "Especially for street food and rural travel" },
    { name: "Japanese Encephalitis", priority: "Recommended for Rural/Long Stays", note: "Risk during monsoon season in rice-growing regions" },
    { name: "Rabies (Pre-Exposure)", priority: "Consider for Backpackers", note: "Recommended for long stays, rural areas, adventure activities" },
    { name: "Hepatitis B", priority: "Recommended", note: "For those at higher risk or long-term stays" },
    { name: "Measles/Mumps/Rubella (MMR)", priority: "Ensure Up-to-Date", note: "Measles outbreaks occur in the region" },
    { name: "Tetanus/Diphtheria/Pertussis", priority: "Ensure Up-to-Date", note: "Booster every 10 years" },
    { name: "COVID-19", priority: "Recommended", note: "Check current country-specific entry requirements" }
  ];

  const medications = [
    "Malaria prophylaxis (if visiting high-risk areas – discuss with clinician)",
    "Travellers' diarrhea treatment kit with antibiotics",
    "Oral rehydration salts",
    "Insect repellent containing DEET or picaridin",
    "Antihistamines for insect bites and allergic reactions",
    "Antifungal cream for skin infections in humid climates"
  ];

  const countries = [
    { name: "Thailand", highlights: ["Bangkok", "Islands", "Northern Hills"] },
    { name: "Vietnam", highlights: ["Hanoi", "Ho Chi Minh", "Mekong Delta"] },
    { name: "Indonesia", highlights: ["Bali", "Java", "Sumatra"] },
    { name: "Cambodia", highlights: ["Angkor Wat", "Phnom Penh", "Siem Reap"] },
    { name: "Laos", highlights: ["Vientiane", "Luang Prabang", "Rural Areas"] },
    { name: "Philippines", highlights: ["Manila", "Palawan", "Cebu"] },
    { name: "Malaysia", highlights: ["Kuala Lumpur", "Borneo", "Penang"] },
    { name: "Myanmar", highlights: ["Yangon", "Bagan", "Inle Lake"] }
  ];

  const preventionTips = [
    {
      icon: <Bug className="w-5 h-5" />,
      tip: "Mosquito Protection",
      detail: "Use DEET repellent, sleep under nets, wear long sleeves at dawn/dusk."
    },
    {
      icon: <Droplets className="w-5 h-5" />,
      tip: "Safe Drinking Water",
      detail: "Drink bottled or purified water only. Avoid ice in drinks."
    },
    {
      icon: <Utensils className="w-5 h-5" />,
      tip: "Food Safety",
      detail: "Eat well-cooked hot food. Peel fruits yourself. Avoid street salads."
    },
    {
      icon: <AlertTriangle className="w-5 h-5" />,
      tip: "Animal Contact",
      detail: "Avoid touching stray animals, especially monkeys and dogs near temples."
    }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-navy-900">
      {/* Hero */}
      <section className="relative py-20 bg-gradient-to-br from-green-600 via-teal-600 to-cyan-700 overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-10" />
        <div className="absolute inset-0">
          <img 
            src="https://images.pexels.com/photos/1371360/pexels-photo-1371360.jpeg?auto=compress&cs=tinysrgb&w=1920"
            alt="Southeast Asia"
            className="w-full h-full object-cover opacity-20"
          />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="max-w-4xl"
          >
            <div className="inline-block px-4 py-2 rounded-full bg-white/20 backdrop-blur-sm border border-white/30 text-white text-sm font-medium mb-6">
              Backpacking • Adventure Travel • Long-Term Stays
            </div>
            <h1 className="text-4xl md:text-6xl font-heading font-bold text-white mb-6">
              Southeast Asia Travel Health
            </h1>
            <p className="text-xl text-white/90 mb-8 leading-relaxed">
              Comprehensive health protection for your Southeast Asia adventure. From bustling cities to remote jungles, we'll help you stay healthy throughout your journey.
            </p>
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-8 py-4 bg-white text-green-600 text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book SEA Travel Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Countries Covered */}
      <section className="py-16 bg-white dark:bg-navy-900 border-b border-gray-200 dark:border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h3 className="text-2xl font-heading font-bold text-navy-900 dark:text-white mb-8 text-center">
            Countries We Cover
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {countries.map((country, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="p-4 bg-gray-50 dark:bg-navy-800 rounded-xl border border-gray-100 dark:border-white/5 hover:border-cyan-500/30 transition-all"
              >
                <h4 className="font-bold text-navy-900 dark:text-white mb-2">{country.name}</h4>
                <div className="flex flex-wrap gap-1">
                  {country.highlights.map((place, i) => (
                    <span key={i} className="text-xs text-gray-600 dark:text-gray-400">
                      {place}{i < country.highlights.length - 1 ? ' •' : ''}
                    </span>
                  ))}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Key Health Risks */}
      <section className="py-20 bg-gray-50 dark:bg-navy-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-navy-900 dark:text-white mb-4">
              Key Health Risks to Discuss
            </h2>
            <p className="text-gray-600 dark:text-gray-400 max-w-2xl mx-auto">
              Southeast Asia's diverse environments present unique health challenges for backpackers and long-term travellers.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {healthRisks.map((risk, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 bg-white dark:bg-navy-900 rounded-2xl shadow-lg border border-gray-100 dark:border-white/5"
              >
                <div className="w-12 h-12 bg-green-100 dark:bg-green-900/20 rounded-xl flex items-center justify-center text-green-600 dark:text-green-400 mb-4">
                  {risk.icon}
                </div>
                <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-3">{risk.title}</h3>
                <p className="text-gray-600 dark:text-gray-400 leading-relaxed">{risk.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Vaccines & Medications */}
      <section className="py-20 bg-white dark:bg-navy-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vaccines */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Vaccines to Discuss with Your Clinician
              </h2>
              <div className="space-y-4">
                {vaccinesDiscuss.map((vaccine, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="p-4 bg-gray-50 dark:bg-navy-800 rounded-xl border border-gray-100 dark:border-white/5 hover:border-cyan-500/30 transition-all"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-bold text-navy-900 dark:text-white">{vaccine.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap ml-2 ${
                        vaccine.priority.includes("Highly") 
                          ? "bg-orange-100 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400"
                          : vaccine.priority.includes("Recommended")
                          ? "bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-400"
                          : "bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300"
                      }`}>
                        {vaccine.priority}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400">{vaccine.note}</p>
                  </motion.div>
                ))}
              </div>

              <div className="mt-6 p-4 bg-cyan-50 dark:bg-cyan-900/10 border border-cyan-200 dark:border-cyan-800 rounded-xl">
                <div className="flex gap-3">
                  <AlertTriangle className="w-5 h-5 text-cyan-600 dark:text-cyan-400 shrink-0 mt-0.5" />
                  <div className="text-sm text-cyan-900 dark:text-cyan-100">
                    <strong>Note:</strong> Some vaccines require multiple doses over several weeks. Book early to ensure full protection before departure.
                  </div>
                </div>
              </div>
            </div>

            {/* Medications & Prevention */}
            <div>
              <h2 className="text-3xl font-heading font-bold text-navy-900 dark:text-white mb-6">
                Medications & Prevention
              </h2>
              <p className="text-gray-600 dark:text-gray-400 mb-6">
                Essential medications and supplies for Southeast Asia travel:
              </p>
              <ul className="space-y-3 mb-8">
                {medications.map((med, index) => (
                  <motion.li
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.05 }}
                    className="flex items-start gap-3"
                  >
                    <CheckCircle2 className="w-5 h-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                    <span className="text-gray-700 dark:text-gray-300">{med}</span>
                  </motion.li>
                ))}
              </ul>

              <h3 className="text-xl font-bold text-navy-900 dark:text-white mb-4">Prevention Best Practices</h3>
              <div className="space-y-3">
                {preventionTips.map((tip, index) => (
                  <div key={index} className="flex items-start gap-3 p-3 bg-gray-50 dark:bg-navy-800 rounded-lg">
                    <div className="w-8 h-8 bg-cyan-100 dark:bg-cyan-900/20 rounded-lg flex items-center justify-center text-cyan-600 dark:text-cyan-400 shrink-0">
                      {tip.icon}
                    </div>
                    <div>
                      <div className="font-bold text-navy-900 dark:text-white text-sm">{tip.tip}</div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">{tip.detail}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* When to Book */}
      <section className="py-20 bg-gradient-to-br from-navy-900 to-navy-800">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <Calendar className="w-16 h-16 text-cyan-400 mx-auto mb-6" />
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-white mb-4">
              When to Book Your Consultation
            </h2>
            <p className="text-gray-300 text-lg">
              Plan ahead for comprehensive protection during your Southeast Asia adventure.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-cyan-400 mb-2">8-12 Weeks</div>
              <div className="text-white font-medium mb-2">Ideal for Long Stays</div>
              <div className="text-sm text-gray-400">Allows time for multi-dose vaccines (Japanese encephalitis, rabies)</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-green-400 mb-2">4-6 Weeks</div>
              <div className="text-white font-medium mb-2">Standard Timeline</div>
              <div className="text-sm text-gray-400">Sufficient for most vaccines and medications</div>
            </div>
            <div className="p-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl text-center">
              <div className="text-4xl font-bold text-orange-400 mb-2">2 Weeks</div>
              <div className="text-white font-medium mb-2">Minimum Notice</div>
              <div className="text-sm text-gray-400">Some protection still possible, but limited options</div>
            </div>
          </div>

          <div className="text-center">
            <button
              onClick={() => setIsBookingModalOpen(true)}
              className="inline-flex items-center gap-2 px-10 py-4 bg-gradient-to-r from-cyan-500 to-blue-600 text-white text-lg font-bold rounded-full shadow-xl hover:shadow-2xl hover:scale-105 transition-all"
            >
              Book Your Southeast Asia Consultation
              <ArrowRight size={20} />
            </button>
            <p className="text-gray-400 text-sm mt-4">
              Virtual consultations available • $60 CAD • Expert travel medicine advice
            </p>
          </div>
        </div>
      </section>

      {/* Booking Modal */}
      <BookingModal 
        isOpen={isBookingModalOpen} 
        onClose={() => setIsBookingModalOpen(false)} 
      />
    </div>
  );
};

export default SoutheastAsia;
